import { DeviceReadingResponseDto, DisplayUnit, EventValueRange } from '@thingslog/repositories';
import { Styles } from '@thingslog/repositories/src/valueRanges/Styles';
import LineChartStyles from '@thingslog/repositories/src/valueRanges/LineChartStyles';
import React, { FC, useMemo, useState } from 'react';
import { LineChart, LineProps, ReferenceLineProps } from '@thingslog/ui-components';
import { format } from 'date-fns';
import { Checkbox, FormControlLabel, Box } from '@mui/material';
import {
  NumericDeviceReadingResponseDto,
  TimeSeriesDataItemDto,
} from '@thingslog/repositories/src/deviceCounter/DeviceReadingsResponseDto';
import { ChartLoadingSkeleton } from '../skeletons/ChartLoadingSkeleton';
import { InfoDisplay } from '../InfoDisplay';
import { ShowChart } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface SensorsDataChartProps {
  data: DeviceReadingResponseDto[];
  eventValueRanges: EventValueRange[];
  displayEventRanges?: boolean;
  isLoading?: boolean;
}

interface ChartDataPoint {
  timestamp: number;
  [key: string]: number | undefined;
}

const CHART_COLORS = [
  '#2196F3',
  '#43A047',
  '#E53935',
  '#FF8C00',
  '#AB47BC',
  '#F2C94C',
  '#2ACFCF',
  '#FF69B4',
  '#795548',
  '#546E7A',
];

export const SensorsDataChart: FC<SensorsDataChartProps> = ({
  data,
  eventValueRanges,
  displayEventRanges = true,
  isLoading = false,
}: SensorsDataChartProps) => {
  const { t } = useTranslation();
  const numericData: NumericDeviceReadingResponseDto[] = useMemo(() => {
    return data.filter(
      (item: DeviceReadingResponseDto): item is NumericDeviceReadingResponseDto =>
        item.sensorData === 'NUMERIC'
    );
  }, [data]);

  const [selectedRanges, setSelectedRanges] = useState<string[]>([]);

  const handleCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    rangeName: string
  ): void => {
    if (event.target.checked) {
      setSelectedRanges((prev: string[]) => [...prev, rangeName]);
    } else {
      setSelectedRanges((prev: string[]) => prev.filter((name: string) => name !== rangeName));
    }
  };

  const getRangeColor = (range: EventValueRange): string => {
    const lineStyle = range.styles?.find((s: Styles) => s['@type'] === 'line') as LineChartStyles;
    return lineStyle?.lineColor || '#000000';
  };

  const referenceLines = useMemo(() => {
    const refLines: ReferenceLineProps[] = [];
    eventValueRanges.forEach((range: EventValueRange) => {
      if (selectedRanges.includes(range.name)) {
        const color = getRangeColor(range);

        if (range.min !== null && range.min !== undefined) {
          refLines.push({
            y: range.min,
            stroke: color,
            strokeDasharray: '3 3',
            label: {
              position: 'insideTopRight',
              value: `${t('sensors_data_chart_min')} ${range.name}`,
              fill: color,
            },
          });
        }
        if (range.max !== null && range.max !== undefined) {
          refLines.push({
            y: range.max,
            stroke: color,
            strokeDasharray: '3 3',
            label: {
              position: 'insideBottomRight',
              value: `${t('sensors_data_chart_max')} ${range.name}`,
              fill: color,
            },
          });
        }
      }
    });
    return refLines;
  }, [eventValueRanges, selectedRanges]);

  const chartData = useMemo(() => {
    if (numericData.length === 0) {
      return [];
    }

    const allTimestamps = new Set<number>();
    numericData.forEach((item: NumericDeviceReadingResponseDto) => {
      item.readings.forEach((reading: TimeSeriesDataItemDto) => {
        allTimestamps.add(new Date(reading.date).getTime());
      });
    });

    const sortedTimestamps = Array.from(allTimestamps).sort((a: number, b: number) => a - b);

    return sortedTimestamps.map((timestamp: number) => {
      const dataPoint: ChartDataPoint = { timestamp };
      numericData.forEach((item: NumericDeviceReadingResponseDto) => {
        const key = `${item.deviceNumber}-${item.sensorName}`;
        const reading = item.readings.find(
          (r: TimeSeriesDataItemDto) => new Date(r.date).getTime() === timestamp
        );
        dataPoint[key] = reading?.value;
      });
      return dataPoint;
    });
  }, [numericData]);

  const lines = useMemo(() => {
    const lineProps: LineProps[] = [];
    numericData.forEach((item: NumericDeviceReadingResponseDto, index: number) => {
      const key = `${item.deviceNumber}-${item.sensorName}`;
      const color = CHART_COLORS[index % CHART_COLORS.length];
      const numberOfDataPoints = item.readings.length;

      lineProps.push({
        type: 'monotone',
        dataKey: key,
        stroke: color,
        color: color,
        name: `${item.deviceName} - ${item.sensorName}`,
        unit: DisplayUnit[item.sensorUnit],
        dot:
          numberOfDataPoints <= 100
            ? {
                stroke: color,
                strokeWidth: 2,
                fill: color,
              }
            : false,
        isAnimationActive: false,
        strokeWidth: 2,
        activeDot: true,
        connectNulls: true,
      });
    });
    return lineProps;
  }, [numericData]);

  if (isLoading) {
    return (
      <div className="h-[500px]">
        <ChartLoadingSkeleton />
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="h-full">
        <InfoDisplay
          icon={<ShowChart />}
          title={t('sensors_data_chart_no_data_available')}
          subtitle={t('sensors_data_chart_no_numeric_data_subtitle')}
        />
      </div>
    );
  }

  return (
    <>
      <div className="h-[400px]">
        <LineChart
          tooltip={{
            labelFormatter: (label: number) => format(label, 'dd/MM HH:mm'),
          }}
          container={{ width: '100%', height: '100%', debounce: 300 }}
          lineChart={{ data: chartData }}
          grid={{ vertical: false, stroke: '#e9e9e9' }}
          xAxis={{
            dataKey: 'timestamp',
            dx: 20,
            domain: ['dataMin', 'dataMax'],
            type: 'number',
            tickMargin: 15,
            tickCount: 15,
            tickLine: true,
            axisLine: true,
            tickFormatter: (value: number): string => `${format(value, 'dd/MM HH:mm')}`,
          }}
          yAxis={{
            tickCount: 10,
            tickMargin: 0,
            axisLine: true,
            tickLine: true,
            type: 'number',
            domain: ['auto', 'auto'],
          }}
          legend={{
            verticalAlign: 'bottom',
          }}
          lines={lines}
          referenceLines={referenceLines}
        />
      </div>
      {displayEventRanges && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: { xs: 1, sm: 2 },
            mt: 2,
            justifyContent: { xs: 'center', sm: 'flex-start' },
          }}
        >
          {eventValueRanges.map((range: EventValueRange) => (
            <FormControlLabel
              key={range.name}
              control={
                <Checkbox
                  checked={selectedRanges.includes(range.name)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                    handleCheckboxChange(e, range.name)
                  }
                  name={range.name}
                  size="small"
                  sx={{
                    '&.Mui-checked': {
                      color: getRangeColor(range),
                    },
                  }}
                />
              }
              label={`${range.name} (${
                numericData.find(
                  (item: NumericDeviceReadingResponseDto) =>
                    item.deviceNumber === range.deviceNumber &&
                    item.sensorIndex === range.sensorIndex
                )?.sensorName || range.sensorIndex
              })`}
              sx={{
                mr: { xs: 1, sm: 2 },
                '& .MuiFormControlLabel-label': {
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                },
              }}
            />
          ))}
        </Box>
      )}
    </>
  );
};
